import JSZip from 'jszip'
import { EnvUtil } from './EnvUtil'
import { DateUtil } from './DateUtil'
import axios, { AxiosResponse } from 'axios'
import { EventEmitter } from './EventEmitter'

type ObjectFit = 'lfit' | 'mfit' | 'fill' | 'pad' | 'fixed'

export class HttpError extends Error {
  constructor(message: string, public code: number, public data: string, public serverMessage: string) {
    super(message)
  }

  get text() {
    if (this.serverMessage !== 'failed') {
      return this.serverMessage || this.textData || this.message
    }

    return this.textData || this.serverMessage || this.message
  }

  get textData() {
    return typeof this.data === 'string' ? this.data : ''
  }
}

export enum HttpUtilEvent {
  BeforeRequest = 'beforeRequest',
  AfterRequest = 'afterRequest',
  StatusCode401 = '401',
  StatusCode500 = '500',
}

export enum HttpUtilMethod {
  Get = 'get',
  Put = 'put',
  Post = 'post',
  Delete = 'delete',
}

export enum HttpStatusCode {
  SC41001 = 41001,
}

export class HttpUtil {
  static withCredentials = false
  static eventEmitter = new EventEmitter()

  // 设定一些全局的处理流程
  static callbackStatus401?: Function
  static callbackStatus500?: Function

  public static on(type: HttpUtilEvent, handler: Function) {
    this.eventEmitter.on(type, handler)
  }

  public static fire(type: HttpUtilEvent) {
    this.eventEmitter.fire(type)
  }

  public static setWithCredentials() {
    this.withCredentials = true
  }

  public static setStatus401Callback(callback: Function) {
    this.callbackStatus401 = callback
  }

  public static setStatus500Callback(callback: Function) {
    this.callbackStatus500 = callback
  }

  public static async request(
    method: HttpUtilMethod,
    url: string,
    params = {},
    headers = {},
    skipEventEmitter = true,
    withCredentials = this.withCredentials
  ): Promise<any> {
    try {
      if (!skipEventEmitter) {
        this.eventEmitter.fire(HttpUtilEvent.BeforeRequest)
      }

      const result = await this[method](url, params, headers, withCredentials)

      return result
    } catch (e) {
      if ((e as any).response) {
        return this.resolveData((e as any).response)
      } else {
        throw e
      }
    } finally {
      if (!skipEventEmitter) {
        this.eventEmitter.fire(HttpUtilEvent.AfterRequest)
      }
    }
  }

  public static async get(
    url: string,
    params = {},
    headers = {},
    withCredentials = this.withCredentials
  ): Promise<any> {
    url = this.buildFullUrl(url)
    const res = await axios.get(url, {
      withCredentials,
      params,
      headers,
    })
    return this.resolveData(res)
  }

  public static async plainGet(
    url: string,
    params = {},
    headers = {},
    withCredentials = this.withCredentials
  ): Promise<any> {
    const res = await axios.get(url, {
      withCredentials,
      params,
      headers,
    })
    return res.data
  }

  public static async basicGet(
    url: string,
    params = {},
    headers = {},
    withCredentials = this.withCredentials
  ): Promise<any> {
    return await axios.get(url, {
      withCredentials,
      params,
      headers,
    })
  }

  public static async post(
    url: string,
    params = {},
    headers = {},
    withCredentials = this.withCredentials
  ): Promise<any> {
    url = this.buildFullUrl(url)
    const res = await axios.post(url, params, {
      withCredentials,
      headers,
    })
    return this.resolveData(res)
  }
  public static async postStream(
    url: string,
    params = {},
    headers = {},
    config = {},
    skipEventEmitter = true,
    withCredentials = this.withCredentials
  ): Promise<any> {
    url = this.buildFullUrl(url)

    if (!skipEventEmitter) {
      this.eventEmitter.fire(HttpUtilEvent.BeforeRequest)
    }
    return axios.post(url, params, {
      withCredentials,
      headers,
      responseType: 'stream',
      adapter: 'fetch',
      ...config,
    })
  }

  public static async put(
    url: string,
    params = {},
    headers = {},
    withCredentials = this.withCredentials
  ): Promise<any> {
    url = this.buildFullUrl(url)
    const res = await axios.put(url, params, {
      withCredentials,
      headers,
    })
    return this.resolveData(res)
  }

  public static async delete(
    url: string,
    params = {},
    headers = {},
    withCredentials = this.withCredentials
  ): Promise<any> {
    url = this.buildFullUrl(url)
    const res = await axios.delete(url, {
      withCredentials,
      params: params,
      headers,
    })
    return this.resolveData(res)
  }

  public static imageAvatar(_url: string, _width = 200, _height = 200, _object_fit: ObjectFit = 'pad') {
    if (_url && _url.includes('http')) {
      const url = new URL(_url)

      url.searchParams.set(
        'x-oss-process',
        `image/resize,limit_0,m_${_object_fit},w_${_width.toFixed(0)},h_${_height.toFixed(
          0
        )}/quality,q_100/auto-orient,1`
      )

      return url.href
    }

    return _url
  }

  public static async fileDownload(url: string): Promise<any> {
    return axios.get(`${process.env.VUE_APP_CRAZYROPE_MANAGE_API}/global/img_proxy?img=${encodeURIComponent(url)}`, {
      responseType: 'blob',
    })
  }

  public static async fileProxy(url: string): Promise<string> {
    return this.fileDownload(url).then((r) => URL.createObjectURL(r.data))
  }

  public static async imageProxy(url: string): Promise<string> {
    let result = url

    if (this.isCrossOrigin(url)) {
      try {
        result = await this.doImageProxy(
          `${process.env.VUE_APP_CRAZYROPE_MANAGE_API}/global/img_proxy?img=${encodeURIComponent(url)}`
        )
      } catch (e) {
        result = url
      }
    }

    return result
  }

  public static doImageProxy(url: string): Promise<string> {
    return axios
      .get(url, { responseType: 'arraybuffer' })
      .then(
        (response) =>
          'data:image/png;base64,' +
          btoa(new Uint8Array(response.data).reduce((data, byte) => data + String.fromCharCode(byte), ''))
      )
  }

  public static buildFullUrl(path: string): string {
    if (path.includes('http')) {
      return path
    }

    if (EnvUtil.isAgency() || EnvUtil.isSaas()) {
      if (location.origin.includes('tiantiantiaosheng.com')) {
        return `${process.env.VUE_APP_API_OLD}${path}`
      }
    }

    return `${process.env.VUE_APP_API}${path}`
  }

  public static resolveData(res: AxiosResponse<any>): any {
    const data = res.data

    if (res.status === 401 || (data && (data.code === 401 || data.error_code === 401))) {
      this.eventEmitter.fire(HttpUtilEvent.StatusCode401, res)

      if (this.callbackStatus401) {
        this.callbackStatus401(res)
      }
    }

    if (res.status === 500) {
      this.eventEmitter.fire(HttpUtilEvent.StatusCode500, res)

      if (this.callbackStatus500) {
        this.callbackStatus500(res)
      }
    }

    if (res.status !== 200) {
      throw new HttpError(`request fail ${data.msg}`, data.code, data.data, data.msg)
    }

    if (!data) {
      if (EnvUtil.isOperation()) {
        return data
      } else {
        throw new HttpError('request fail', 0, '', '')
      }
    }

    if ('isOk' in data) {
      if (data.isOk) {
        return data
      } else {
        throw new HttpError(`request fail ${data.message}`, data.status_code, data, data.message)
      }
    } else if ('code' in data) {
      if (data.code === 0) {
        return data.data
      } else {
        if (EnvUtil.isApph5() && data.code === HttpStatusCode.SC41001) {
          this.eventEmitter.fire(HttpUtilEvent.StatusCode401, res)
        }

        throw new HttpError(`request fail ${data.msg}`, data.code, data.data, data.msg)
      }
    } else {
      if (data.code === undefined) {
        return data
      } else {
        throw new HttpError(`request fail ${data.msg}`, data.code, data.data, data.msg)
      }
    }
  }

  /**
   * 单例请求，避免同一时间多次访问
   * _singletonBlockMap 存一些正在请求的标记
   */
  public static _singletonBlockMap: { [key: string]: boolean } = {}
  public static async singletonRequestBlock(blockName: string, requestFN: Function) {
    for (let i = 0; i < 10; i++) {
      if (this._singletonBlockMap[blockName]) {
        await DateUtil.sleep(400)
      } else {
        break
      }
    }
    this._singletonBlockMap[blockName] = true
    await requestFN()
    this._singletonBlockMap[blockName] = false
  }

  /**
   * 缓存20秒内相关请求不发送了，注意使用的时候需要拿到返回值
   */
  public static _cacheSingletonBlockMap: { [key: string]: any } = {}
  public static _cacheSingletonBlockMapFire: { [key: string]: boolean } = {}
  public static _cacheSingletonBlockMapTime: { [key: string]: Date } = {}
  public static async cacheSingletonRequestBlock(blockName: string, requestFN: Function): Promise<any> {
    for (let i = 0; i < 10; i++) {
      if (this._cacheSingletonRequestBlockCheckHasValue(blockName)) {
        return this._cacheSingletonBlockMap[blockName]
      }
      if (this._cacheSingletonBlockMapFire[blockName]) {
        await DateUtil.sleep(400)
      } else {
        break
      }
    }
    this._cacheSingletonBlockMapFire[blockName] = true
    const result = await requestFN()
    this._cacheSingletonBlockMap[blockName] = result
    this._cacheSingletonBlockMapTime[blockName] = new Date()
    this._cacheSingletonBlockMapFire[blockName] = false
    return result
  }

  public static _cacheSingletonRequestBlockCheckHasValue(blockName: string) {
    const value = this._cacheSingletonBlockMap[blockName]
    const setTime = this._cacheSingletonBlockMapTime[blockName]
    if (value && setTime) {
      return new Date().getTime() - setTime.getTime() < 20_000 // 缓存时间为20秒
    }
    return false
  }

  public static isCrossOrigin(url: string): boolean {
    return (
      url.startsWith('http') &&
      !url.startsWith(window.origin || location.origin || `${location.protocol}://${location.host}`)
    )
  }

  public static batchDownload(sources: { url: string; title: string; type?: string }[]) {
    const zip = new JSZip()
    const promises: Promise<any>[] = []

    sources.forEach((s) => {
      const p = this.fileDownload(s.url).then((res) => {
        const temp = s.url.split('.')
        const suffix = temp[temp.length - 1]
        zip.file(s.title + '.' + (s.type ? s.type : suffix), res.data, { binary: true })
      })

      promises.push(p)
    })

    return Promise.all(promises).then(() => {
      return zip.generateAsync({
        type: 'blob',
        compression: 'DEFLATE',
        compressionOptions: {
          level: 9,
        },
      })
    })
  }
}
